import {useAppContext} from "../../contexts/AppContext";
import {useEffect, useState} from "react";
import {requester} from "../../services/requester";
import baseUrl from "../../baseUrl";
import {Link} from "react-router-dom";
import {DataUploadRow} from "./DataUploadRow/DataUploadRow";
import {BulkImport} from "./BulkImport/BulkImport";
import {ArchiveSection} from "./Archive/ArchiveSection";
import {useConfirm} from "../../contexts/ConfirmContext";
import {decodeFileContent, downloadCsvTemplate, toNumber, CSV_DELIMITER,
    parseInitialImportCsv, INITIAL_IMPORT_COLUMNS} from "../../utils/csv";

// Р5: шаблонът за сваляне вече е с български заглавия. Старите файлове с
// английски също се качват — синонимите са в COL_ALIASES (utils/csv.js).
const INITIAL_TEMPLATE_HEADER = INITIAL_IMPORT_COLUMNS.join(CSV_DELIMITER);

export const DataUpload = () => {
    const {companiesBullstat, settlementsEkatte} = useAppContext();
    const allowedExtensions = ["csv"];
    const [file, setFile] = useState("");
    const [dataParsed, setDataParsed] = useState({})
    const [dataColumn, setDataColumn] = useState([]);
    const [error, setError] = useState("");
    const [years, setYears] = useState([])
    const [settlementsIdsEdit, setSettlementsIdsEdit] = useState([])
    const [settlementsIdsDelete, setSettlementsIdsDelete] = useState([])
    const [dataForEdit, setDataForEdit] = useState([])
    const [isShowUpload, setIsShowUpload] = useState(true);
    const [uploadSummary, setUploadSummary] = useState(null);
    const [valueSettlementDelete, setValueSettlementDelete] = useState('')
    const [valueSettlementEdit, setValueSettlementEdit] = useState('')
    const [valueYearDelete, setValueYearDelete] = useState('')
    const [valueYearEdit, setValueYearEdit] = useState('')
    const request = requester();
    const confirm = useConfirm();

    // Налични години за падащите списъци
    useEffect(() => {
        request.get(`${baseUrl()}/years`)
            .then(data => setYears(data));
    }, []);

    // EKATTE списък за секция "Редакция" според избраната година
    useEffect(() => {
        if (!valueYearEdit) {
            setSettlementsIdsEdit([]);
            return;
        }
        request.get(`${baseUrl()}/companies-initial-info/${valueYearEdit}/ids`)
            .then(data => setSettlementsIdsEdit(data));
    }, [valueYearEdit]);

    // EKATTE списък за секция "Изтриване" според избраната година
    useEffect(() => {
        if (!valueYearDelete) {
            setSettlementsIdsDelete([]);
            return;
        }
        request.get(`${baseUrl()}/companies-initial-info/${valueYearDelete}/ids`)
            .then(data => setSettlementsIdsDelete(data));
    }, [valueYearDelete, valueSettlementDelete]);

    const handleFileChange = (e) => {
        setError("");

        if (e.target.files.length) {
            const inputFile = e.target.files[0];

            const fileExtension = inputFile?.type.split("/")[1];
            if (!allowedExtensions.includes(fileExtension)) {
                setError("Моля изберете csv файл");
                return;
            }


            setFile(inputFile)
        }
    };

    const handleParse = () => {

        if (!file) return setError("Не е избран файл");

        const reader = new FileReader();


        reader.onload = async ({target}) => {
            // Р5: разчитането минава през общия парсер (utils/csv.js) — същия,
            // който ползват другите два вноса. Той разпознава заглавията без
            // значение от регистър и реда им, приема и българските, и старите
            // английски имена, и при разминаване казва КОЯ колона липсва вместо
            // „колоните не съвпадат, презаредете страницата".
            const parsed = parseInitialImportCsv(decodeFileContent(target.result));
            setUploadSummary(null);

            if (parsed.error) {
                setDataParsed({});
                setDataColumn([]);
                setError(parsed.error);
                return;
            }

            const parsedData = parsed.rows;
            setDataParsed(parsedData);
            setDataColumn(INITIAL_IMPORT_COLUMNS);

            const newCompanies = [];
            parsedData.forEach(company => {
                if (company['bullstat'] && !companiesBullstat.includes(Number(company['bullstat']))) {
                    const bs = Number(company['bullstat']);
                    if (!newCompanies.find(c => c.bullstat === bs)) {
                        newCompanies.push({bullstat: bs, company_name: company['company_name']});
                    }
                }
            })

            for (const company of newCompanies) {
                try {
                    await request.post(`${baseUrl()}/companies`, company);
                    companiesBullstat.push(company.bullstat);
                } catch (e) {
                    if (!e.message?.includes('вече съществува')) {
                        setError(`Грешка при добавяне на ползвател ${company.bullstat}: ${e.message}`);
                        return;
                    }
                }
            }

            const ekatte_not_in_base = [];
            parsedData.forEach(company => {
                if (!settlementsEkatte.includes(company['settlement_ekatte'])) {
                    ekatte_not_in_base.push(company['settlement_ekatte'])
                }
            })

            if (ekatte_not_in_base.length > 0) {
                setError(`Следните ekatte номера ${ekatte_not_in_base.join(', ')} не фигурират в базата данни. За да заредите файл с данни, трябва да се попълни реален ekatte номер. Коригирайте и опитайте отново.`)
                return;
            }

            setIsShowUpload(false)
        };
        reader.readAsArrayBuffer(file);
    };

    const handleUpload = () => {
        // Инкрементално качване: новите редове се добавят, вече съществуващите
        // (сървърът връща 400 "there is same entry") се прескачат без дубликат.
        const rows = dataParsed
            .filter(row => row["bullstat"])
            .map(row => ({
                // Ж3: toNumber приема и десетична запетая — така файл, записан
                // от български Excel с разделител „;", минава без ръчна правка.
                bullstat: toNumber(row["bullstat"]),
                company_name: row["company_name"],
                declared_land: toNumber(row["declared_land"]),
                unsuitable_land: toNumber(row["unsuitable_land"]),
                suitable_land: toNumber(row["suitable_land"]),
                year: toNumber(row["year"]),
                settlement_ekatte: row["settlement_ekatte"],
            }));

        Promise.all(rows.map(body =>
            request.post(`${baseUrl()}/companies-initial-info`, body)
                .then(() => ({status: "added"}))
                .catch(err => {
                    if (err?.message === "there is same entry") {
                        return {status: "skipped"};
                    }
                    return {status: "error", bullstat: body.bullstat, message: err?.message || "неизвестна грешка"};
                })
        )).then(results => {
            setUploadSummary({
                total: rows.length,
                added: results.filter(r => r.status === "added").length,
                skipped: results.filter(r => r.status === "skipped").length,
                errors: results.filter(r => r.status === "error"),
            });
            setIsShowUpload(true);
            setDataColumn([]);
            setFile("");
            const input = document.getElementById("csvInput");
            if (input) input.value = "";
        });
    }

    const onChangeYearDelete = (event) => {
        setValueYearDelete(event.target.value);
        setValueSettlementDelete('');
    };

    const onChangeSettlementDelete = (event) => {
        setValueSettlementDelete(event.target.value);
    };

    const handleDelete = async () => {
        // Р7: това е НАЙ-разрушителното действие в приложението — маха правното
        // основание на ВСИЧКИ ползватели в едно землище за една година, а досега
        // нямаше никакво предупреждение. Затова диалогът първо преброява какво
        // ще падне и го казва с числото; „сигурен ли сте" без число не помага.
        let брой = null;
        try {
            const редове = await request.get(
                `${baseUrl()}/companies-initial-info/${valueYearDelete}/${valueSettlementDelete}`);
            брой = Array.isArray(редове) ? редове.length : null;
        } catch (e) {
            брой = null;   // не спираме заради броенето — само няма да е в текста
        }

        if (!await confirm({
            title: "Да се изтрият ли данните за цялото землище?",
            message: "Премахва се правното основание на ВСИЧКИ ползватели в това "
                + "землище за избраната година.",
            detail: `${valueSettlementDelete} · ${valueYearDelete} г.`
                + (брой !== null ? ` · ${брой} ползвателя` : ""),
            confirmText: "Да, изтрий всичко",
        })) return;

        request.delete(`${baseUrl()}/companies-initial-info/${valueYearDelete}/${valueSettlementDelete}`)
            .then(() => {
                setValueSettlementDelete('')
            })
    };

    const onChangeYearEdit = (event) => {
        setValueYearEdit(event.target.value);
        setValueSettlementEdit('');
        setDataForEdit([]);
    };

    const onChangeSettlementEdit = (event) => {
        setValueSettlementEdit(event.target.value);
        setDataForEdit('')
    };

    const handleEditData = () => {
        request.get(`${baseUrl()}/companies-initial-info/${valueYearEdit}/${valueSettlementEdit}`)
            .then(data => {
                setDataForEdit(data)
            })
    }


    return (
        <>
            <div style={{
                background: "var(--app-surface-2)",
                border: "1px solid var(--app-gold)",
                borderRadius: "6px",
                padding: "16px 20px",
                marginTop: "16px",
            }}>
                <h5 style={{color: "var(--app-gold)", marginBottom: "10px"}}>ℹ️ Как работи качването на данни</h5>
                <p style={{marginBottom: "8px"}}>
                    <strong>Качването е „инкрементално"</strong> — добавят се само новите редове. Ако ред за
                    същата комбинация <strong>година + булстат + EKATTE</strong> вече съществува в базата,
                    той се <strong>прескача</strong> и <strong>не се дублира</strong>.
                </p>
                <ul style={{marginBottom: "10px"}}>
                    <li><strong>Не е нужно да триете преди повторно качване.</strong> Ако презаредите файл за
                        вече качено село, само липсващите (нови) редове ще се добавят, а съществуващите ще се
                        пропуснат.</li>
                    <li>След всяко качване се показва <strong>резюме</strong>: колко реда са <em>добавени</em>,
                        колко <em>вече съществуващи (пропуснати)</em> и колко <em>грешки</em> (ако има — с
                        булстат и причина).</li>
                </ul>
                <p style={{marginBottom: "6px"}}><strong>Кога кое да ползвате:</strong></p>
                <ul style={{marginBottom: "10px"}}>
                    <li>✏️ <strong>Редакция на вече съществуващи данни</strong> (напр. поправка на площ или
                        име) → използвайте секцията <strong>„Редакция на данни в базата"</strong> по-долу.
                        Качването на файл <strong>не променя</strong> вече съществуващи редове.</li>
                    <li>🗑️ <strong>Премахване на цяло село за дадена година</strong> → използвайте секцията
                        <strong> „Изтриване на данни от базата"</strong> по-долу.</li>
                </ul>
                <p style={{marginBottom: "6px"}}><strong>Изисквания към файла:</strong></p>
                <ul style={{marginBottom: 0}}>
                    <li>Формат <strong>CSV</strong> с колони:{' '}
                        <code>Булстат; Ползвател; Декларирана; Необработваема; Годна; Година; ЕКАТТЕ</code>.
                        Площите са в декари.</li>
                    <li>ЕКАТТЕ номерът трябва да съществува в базата (реално землище).</li>
                    <li>Старите файлове с английски заглавия (<code>bullstat, company_name…</code>)
                        също се качват — не е нужно да ги преправяте.</li>
                </ul>
            </div>
            <div className="row g-3 align-items-center mt-1">
                <h3 style={{textDecoration: "underline"}}>Зареждане на файл с данни</h3>
                <div className="col-3">
                    <label className="form-label" htmlFor="csvInput">
                        Избери csv файл
                    </label>
                </div>
                <div className="col-6">
                    <input
                        onChange={handleFileChange}
                        id="csvInput"
                        name="file"
                        type="File"
                        className="form-control"
                    />
                </div>
                <p style={{fontStyle: "italic", textDecoration: "underline"}}><Link target="_blank" to={'/upload-help'}>Указания за качване на файл с данни</Link></p>
                <div className="col-12">
                    <button type="button" className="btn btn-outline-secondary btn-sm"
                            onClick={() => downloadCsvTemplate(INITIAL_TEMPLATE_HEADER, "shablon-danni.csv")}>
                        ⬇ Свали шаблон (.csv)
                    </button>
                    <div className="text-muted mt-1" style={{fontSize: "0.85rem"}}>
                        Разделител — точка и запетая или запетая (разпознава се автоматично).
                        Десетичните числа може да са с точка или със запетая.
                    </div>
                </div>
                {file && (
                    <div>
                        <button className="btn btn-secondary" onClick={handleParse}>Зареди</button>
                    </div>
                )}

                <div style={{marginTop: "3rem", color: "var(--app-danger-fg)", fontWeight: "bold"}}>
                    {error ? error : ""}
                </div>
                {/* Р5: таблиците „Колони на заредена таблица" и „Изискващи се
                    колони" отпадат. Те съществуваха, защото шапката беше на
                    английски и човек трябваше да сверява дума по дума. Сега
                    имената са български и съвпадат с това, което пише на
                    екраните; при разминаване парсерът казва точно коя колона
                    липсва и какво е намерил във файла. */}
                {dataColumn.length > 0 && (
                    <p className="mt-3" style={{color: "var(--app-muted)"}}>
                        Разчетени колони: {dataColumn.join(" · ")}
                    </p>
                )}

                {(error || dataColumn.length === 0 || isShowUpload) ? "" : (
                    <>
                        <ul className="list-group">
                            <li className="list-group-item list-group-item-secondary">За качване в базата:</li>
                            <li className="list-group-item">Брой редове на таблицата: {dataParsed.length}</li>
                            <li className="list-group-item">Година: {dataParsed[0]["year"]}</li>
                            <li className="list-group-item">Селище EKKATE: {dataParsed[0]["settlement_ekatte"]}</li>
                        </ul>

                        <h6 style={{color: "var(--app-danger-fg)", fontStyle: "italic"}}>Ако желаете да заредите друга таблица, първо
                            ПРЕЗАРЕДЕТЕ страницата!</h6>
                        <div>
                            <button className="btn btn-secondary" onClick={handleUpload}>Качи в базата</button>
                        </div>
                    </>
                )}
                {uploadSummary && (
                    <div className="mt-3">
                        <ul className="list-group">
                            <li className="list-group-item list-group-item-secondary">Резултат от качването:</li>
                            <li className="list-group-item">Редове във файла: {uploadSummary.total}</li>
                            <li className="list-group-item text-success fw-bold">Добавени нови: {uploadSummary.added}</li>
                            <li className="list-group-item">Вече съществуващи (пропуснати): {uploadSummary.skipped}</li>
                            {uploadSummary.errors.length > 0 && (
                                <li className="list-group-item text-danger">
                                    Грешки: {uploadSummary.errors.length}
                                    <ul>
                                        {uploadSummary.errors.map((e, index) => (
                                            <li key={index}>булстат {e.bullstat}: {e.message}</li>
                                        ))}
                                    </ul>
                                </li>
                            )}
                        </ul>
                        <button className="btn btn-primary mt-2" onClick={() => window.location.reload()}>
                            Презареди (визуализирай новата база)
                        </button>
                    </div>
                )}
                <hr/>
                {/* Л — масов внос от стари бази. Стои ТУК, а не в отделен екран
                    само за админ: така е и за всеки потребител (правата пак са
                    по землище), а „Администрация → Данни" преизползва точно
                    този компонент и секциите излизат и там. */}
                <div className="col-12">
                    <BulkImport kind="deals"/>
                    <BulkImport kind="drawings"/>
                </div>

                <hr/>
                <h3 style={{textDecoration: "underline"}}>Редакция на данни в базата</h3>
                <div className="row g-3 align-items-center mt-1">
                    <div className="col-auto">
                        <label htmlFor="year-edit" className="col-form-label">Година:</label>
                    </div>
                    <div className="col-auto">
                        <select id="year-edit" className="form-select" style={{width: "auto"}}
                                value={valueYearEdit} onChange={onChangeYearEdit}>
                            <option value="">-- изберете --</option>
                            {years.map(y => <option key={y.id} value={y.year}>{y.year}</option>)}
                        </select>
                    </div>
                    {valueYearEdit && (
                        <>
                            <div className="col-auto">
                                <label htmlFor="settlements-edit" className="col-form-label">EKATTE:</label>
                            </div>
                            <div className="col-auto">
                                <input id="settlements-edit" className="form-control" style={{width: "180px"}}
                                       type="search" list="list-settlements-edit" autoComplete="on"
                                       value={valueSettlementEdit}
                                       onChange={onChangeSettlementEdit}/>
                                <datalist id="list-settlements-edit">
                                    {settlementsIdsEdit.map(d => <option key={d.id} value={d.settlement_ekatte}/>)}
                                </datalist>
                            </div>
                        </>
                    )}
                    {valueSettlementEdit && (
                        <div className="col-auto">
                            <button className="btn btn-secondary" onClick={handleEditData}>Зареди</button>
                        </div>
                    )}
                    {dataForEdit.length > 0 && (
                        <table className="table">
                            <thead>
                            <tr>
                                <th>id</th>
                                <th>bullstat</th>
                                <th>company_name</th>
                                <th>settlement_ekatte</th>
                                <th>declared_land</th>
                                <th>unsuitable_land</th>
                                <th>suitable_land</th>
                                <th>year</th>
                                <th></th>
                                <th></th>
                            </tr>
                            </thead>
                            <tbody>
                            {dataForEdit.sort((a, b) => a.id - b.id).map((data, index) => (
                                <DataUploadRow key={index} data={data} index={index} handleEditData={handleEditData}/>
                            ))}
                            </tbody>
                        </table>
                    )}
                </div>

                <hr/>
                {/* Ф2: архивирането стои между редакцията и изтриването —
                    изтриването остава последно, както е било. */}
                <h3 style={{textDecoration: "underline"}}>Архивиране</h3>
                <ArchiveSection years={years}/>

                <hr/>
                <h3 style={{color: "var(--app-danger-fg)", textDecoration: "underline"}}>Изтриване на данни от базата</h3>
                <div className="row g-3 align-items-center mt-1">
                    <div className="col-auto">
                        <label htmlFor="year-delete" className="col-form-label">Година:</label>
                    </div>
                    <div className="col-auto">
                        <select id="year-delete" className="form-select" style={{width: "auto"}}
                                value={valueYearDelete} onChange={onChangeYearDelete}>
                            <option value="">-- изберете --</option>
                            {years.map(y => <option key={y.id} value={y.year}>{y.year}</option>)}
                        </select>
                    </div>
                    {valueYearDelete && (
                        <>
                            <div className="col-auto">
                                <label htmlFor="settlements-delete" className="col-form-label">EKATTE:</label>
                            </div>
                            <div className="col-auto">
                                <input id="settlements-delete" className="form-control" style={{width: "180px"}}
                                       type="search" list="list-settlements-delete" autoComplete="on"
                                       value={valueSettlementDelete}
                                       onChange={onChangeSettlementDelete}/>
                                <datalist id="list-settlements-delete">
                                    {settlementsIdsDelete.map(d => <option key={d.id} value={d.settlement_ekatte}/>)}
                                </datalist>
                            </div>
                        </>
                    )}
                    {valueSettlementDelete && (
                        <div className="col-auto">
                            <button className="btn btn-danger" onClick={handleDelete}>Изтрий от базата</button>
                        </div>
                    )}
                </div>
            </div>
        </>
    )
}